// client/src/pages/GarageDashboardPage.jsx
import { useNavigate } from 'react-router-dom'
import { Briefcase, Users, Wrench, Package, ChevronRight, ArrowRight, AlertCircle, UserPlus, Search } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useJobs, useCustomers, useParts, useJobPartsAlerts, useGarageRequests, useRespondToGarageRequest } from '../api/hooks'

// ─── Constants ────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  critical: 'bg-red-500/15 text-red-400 border border-red-500/30',
  high:     'bg-orange-500/15 text-orange-400 border border-orange-500/30',
  medium:   'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  low:      'bg-blue-500/15 text-blue-400 border border-blue-500/30',
}

const STATUS_STYLES = {
  requested:         'bg-blue-500/15 text-blue-400',
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
  cancelled:         'bg-gray-500/15 text-gray-400',
}

const STATUS_LABELS = {
  requested: 'Requested', quoted: 'Quoted', awaiting_approval: 'Awaiting Approval',
  approved: 'Approved', in_progress: 'In Progress', waiting_on_parts: 'Waiting on Parts',
  completed: 'Completed', cancelled: 'Cancelled',
}

// Phases shown on dashboard, in priority order
const JOB_PHASES = [
  {
    key:    'requested',
    label:  'New Requests',
    dot:    'bg-blue-400 animate-pulse',
    badge:  'bg-blue-500/15 text-blue-400',
    note:   'Awaiting your review',
    urgent: true,
  },
  {
    key:    'awaiting_approval',
    label:  'Awaiting Customer Approval',
    dot:    'bg-yellow-400',
    badge:  'bg-yellow-500/15 text-yellow-400',
    note:   'Quote sent — waiting on customer',
    urgent: false,
  },
  {
    key:    'in_progress',
    label:  'In Progress',
    dot:    'bg-amber-400',
    badge:  'bg-amber-500/15 text-amber-400',
    note:   null,
    urgent: false,
  },
  {
    key:    'waiting_on_parts',
    label:  'Waiting on Parts',
    dot:    'bg-orange-400',
    badge:  'bg-orange-500/15 text-orange-400',
    note:   'Blocked — parts not yet received',
    urgent: false,
  },
  {
    key:    'approved',
    label:  'Approved — Ready to Start',
    dot:    'bg-indigo-400',
    badge:  'bg-indigo-500/15 text-indigo-400',
    note:   null,
    urgent: false,
  },
  {
    key:    'quoted',
    label:  'Quoted',
    dot:    'bg-purple-400',
    badge:  'bg-purple-500/15 text-purple-400',
    note:   null,
    urgent: false,
  },
]

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, colour, icon: Icon, onClick }) {
  const colours = {
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    amber:  'bg-amber-500/10  text-amber-400  border-amber-500/20',
    blue:   'bg-blue-500/10   text-blue-400   border-blue-500/20',
    green:  'bg-green-500/10  text-green-400  border-green-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    gray:   'bg-gray-500/10   text-gray-400   border-gray-500/20',
  }
  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left p-5 rounded-xl border transition-all',
        onClick ? 'hover:border-[var(--border-strong)] cursor-pointer' : 'cursor-default',
        colours[colour] || colours.gray
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} />
        {onClick && <ArrowRight size={14} className="opacity-60" />}
      </div>
      <p className="font-display text-3xl font-bold">{value}</p>
      <p className="text-xs font-medium mt-1 opacity-80">{label}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </button>
  )
}

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

// ─── Job row ──────────────────────────────────────────────────────────────────

function JobRow({ job, onClick }) {
  const sev = job.highest_issue_severity || job.priority
  const isCritical = sev === 'critical'
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-4 px-5 py-3.5 bg-[var(--bg-surface)] border rounded-xl',
        'hover:border-[var(--border-strong)] transition-all text-left group',
        isCritical ? 'border-red-500/40' : 'border-[var(--border)]'
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {job.registration && <Plate registration={job.registration} />}
          <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
            {job.title}
          </p>
          {job.needs_investigation && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium
                             bg-purple-500/15 text-purple-400 border border-purple-500/30">
              <Search size={10} />
              Investigate
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {sev && (
            <span className={clsx('px-1.5 py-0.5 rounded text-xs font-medium capitalize', SEVERITY_STYLES[sev])}>
              {sev}
            </span>
          )}
          {job.date_booked && (
            <p className="text-xs text-[var(--text-muted)]">
              {new Date(job.date_booked).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </p>
          )}
        </div>
      </div>
      <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400 shrink-0" />
    </button>
  )
}

// ─── Phase section ────────────────────────────────────────────────────────────

function PhaseSection({ phase, jobs, onJobClick, onViewAll }) {
  if (jobs.length === 0) return null
  const preview = jobs.slice(0, 5)
  const overflow = jobs.length - preview.length

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={clsx('w-2 h-2 rounded-full shrink-0', phase.dot)} />
          <h3 className="font-medium text-[var(--text-primary)]">{phase.label}</h3>
          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', phase.badge)}>
            {jobs.length}
          </span>
        </div>
        {jobs.length > 0 && (
          <button
            onClick={onViewAll}
            className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            View all <ChevronRight size={13} />
          </button>
        )}
      </div>
      {phase.note && (
        <p className="text-xs text-[var(--text-muted)] mb-2 ml-4">{phase.note}</p>
      )}
      <div className="space-y-2">
        {preview.map(job => (
          <JobRow key={job.id} job={job} onClick={() => onJobClick(job.id)} />
        ))}
        {overflow > 0 && (
          <button
            onClick={onViewAll}
            className="w-full py-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Membership request card ──────────────────────────────────────────────────

function MembershipRequestCard({ request, onRespond, isPending }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{request.user_name}</p>
          <p className="text-xs text-[var(--text-muted)]">{request.user_email}</p>

          {request.vehicles?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {request.vehicles.map(v => (
                <span key={v.id}
                  className="inline-flex items-center gap-1.5 text-xs bg-[var(--bg-base)]
                             border border-[var(--border)] px-2 py-1 rounded-lg">
                  {v.registration && <Plate registration={v.registration} />}
                  <span className="text-[var(--text-secondary)]">
                    {v.make} {v.model}{v.year ? ` (${v.year})` : ''}
                  </span>
                </span>
              ))}
            </div>
          )}

          {request.message && (
            <p className="text-sm text-[var(--text-secondary)] mt-2 italic">
              "{request.message}"
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={() => onRespond(request.id, 'approved')}
            disabled={isPending}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50
                       text-white text-xs font-medium rounded-lg transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() => onRespond(request.id, 'declined')}
            disabled={isPending}
            className="px-3 py-1.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                       text-[var(--text-secondary)] text-xs font-medium rounded-lg transition-colors"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function GarageDashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: allJobs = [], isLoading: jobsLoading } = useJobs()
  const { data: customers = [] }          = useCustomers()
  const { data: parts = [] }              = useParts()
  const { data: partsAlerts = [] }        = useJobPartsAlerts()
  const { data: membershipRequests = [] } = useGarageRequests()
  const respondToRequest                  = useRespondToGarageRequest()

  const activeJobs     = allJobs.filter(j => !['completed', 'cancelled'].includes(j.status))
  const requestedJobs  = allJobs.filter(j => j.status === 'requested')
  const inProgressJobs = allJobs.filter(j => j.status === 'in_progress')
  const awaitingJobs   = allJobs.filter(j => j.status === 'awaiting_approval')

  const thisMonth = new Date()
  thisMonth.setDate(1)
  const completedThisMonth = allJobs.filter(
    j => j.status === 'completed' && j.date_completed && new Date(j.date_completed) >= thisMonth
  )

  const jobsByPhase = Object.fromEntries(
    JOB_PHASES.map(p => [p.key, allJobs.filter(j => j.status === p.key)])
  )
  const hasAnyActive = JOB_PHASES.some(p => jobsByPhase[p.key].length > 0)

  const handleRespond = async (id, status) => {
    try {
      await respondToRequest.mutateAsync({ id, status })
      toast.success(status === 'approved' ? 'Customer registered' : 'Request declined')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to respond')
    }
  }

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          Garage Dashboard
        </h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          {user?.name ? `Welcome back, ${user.name.split(' ')[0]}.` : 'Good to see you.'}
        </p>
      </div>

      {/* Membership requests */}
      {membershipRequests.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={18} className="text-indigo-400" />
            <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">
              Membership Requests
            </h2>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-500/15 text-indigo-400">
              {membershipRequests.length}
            </span>
          </div>
          <div className="space-y-3">
            {membershipRequests.map(req => (
              <MembershipRequestCard
                key={req.id}
                request={req}
                onRespond={handleRespond}
                isPending={respondToRequest.isPending}
              />
            ))}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard
          label="Active Jobs"
          value={jobsLoading ? '–' : activeJobs.length}
          colour="indigo"
          icon={Briefcase}
          onClick={() => navigate('/garage/jobs')}
        />
        <KpiCard
          label="Requested"
          value={jobsLoading ? '–' : requestedJobs.length}
          sub={requestedJobs.length > 0 ? 'Awaiting review' : undefined}
          colour="blue"
          icon={Wrench}
          onClick={() => navigate('/garage/jobs?status=requested')}
        />
        <KpiCard
          label="In Progress"
          value={jobsLoading ? '–' : inProgressJobs.length}
          colour="amber"
          icon={Wrench}
          onClick={() => navigate('/garage/jobs?status=in_progress')}
        />
        <KpiCard
          label="Completed This Month"
          value={jobsLoading ? '–' : completedThisMonth.length}
          colour="green"
          icon={Briefcase}
        />
      </div>

      {/* Catalogue quick-stats */}
      <div className="grid grid-cols-2 gap-4">
        <KpiCard
          label="Customers"
          value={customers.length}
          colour="purple"
          icon={Users}
          onClick={() => navigate('/garage/customers')}
        />
        <KpiCard
          label="Parts in Catalogue"
          value={parts.length}
          colour="gray"
          icon={Package}
          onClick={() => navigate('/garage/parts')}
        />
      </div>

      {/* Jobs by phase */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Jobs</h2>
          <button
            onClick={() => navigate('/garage/jobs')}
            className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            View all <ChevronRight size={15} />
          </button>
        </div>

        {jobsLoading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : !hasAnyActive ? (
          <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
            <p className="text-sm text-[var(--text-muted)]">No active jobs. Create one from the Jobs page.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {JOB_PHASES.map(phase => (
              <PhaseSection
                key={phase.key}
                phase={phase}
                jobs={jobsByPhase[phase.key]}
                onJobClick={(jobId) => navigate(`/garage/jobs/${jobId}`)}
                onViewAll={() => navigate(`/garage/jobs?status=${phase.key}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Parts needing attention */}
      {partsAlerts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Parts Needed</h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400">
                {partsAlerts.length}
              </span>
            </div>
            <button
              onClick={() => navigate('/garage/jobs')}
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View jobs <ChevronRight size={15} />
            </button>
          </div>
          <div className="space-y-2">
            {partsAlerts.map(part => (
              <button
                key={part.id}
                onClick={() => navigate(`/garage/jobs/${part.job_id}`)}
                className="w-full flex items-center gap-4 px-5 py-3.5 bg-[var(--bg-surface)] border border-[var(--border)]
                           rounded-xl hover:border-[var(--border-strong)] transition-all text-left group"
              >
                <AlertCircle size={16} className={clsx(
                  'shrink-0',
                  part.status === 'required' ? 'text-red-400' : 'text-amber-400'
                )} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
                    {part.name}
                    {part.quantity > 1 && (
                      <span className="text-[var(--text-muted)] font-normal"> ×{part.quantity}</span>
                    )}
                  </p>
                  <p className="text-xs text-[var(--text-muted)] truncate">
                    {part.job_title}
                    {part.registration && ` · ${part.registration} ${part.make} ${part.model}`}
                  </p>
                </div>
                <span className={clsx(
                  'shrink-0 px-2 py-0.5 rounded text-xs font-medium capitalize',
                  part.status === 'required'
                    ? 'bg-red-500/15 text-red-400'
                    : 'bg-amber-500/15 text-amber-400'
                )}>
                  {part.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div>
        <h2 className="font-display text-xl font-bold text-[var(--text-primary)] mb-3">Quick Links</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: 'New Job',          path: '/garage/jobs',      note: 'Create a work order'   },
            { label: 'Parts Catalogue',  path: '/garage/parts',     note: 'Manage stock items'    },
            { label: 'Customers',        path: '/garage/customers', note: 'View customer records' },
          ].map(({ label, path, note }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="text-left p-4 bg-[var(--bg-surface)] border border-[var(--border)]
                         hover:border-[var(--border-strong)] rounded-xl transition-all group"
            >
              <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
                {label}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">{note}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
